import { PROPERTIES_API_URL } from '@src/v3/constants/urls';
import {
  Collaborator,
  CreatePropertyPayload,
} from '@src/v3/interfaces/app/agent/addProperty';
import {
  ClosePropertyProps,
  ModifyDocumentsProperty,
  UpdatePropertyPayload,
} from '@src/v3/interfaces/app/agent/edit';
import {
  DeleteUnsaveRenterSyncPayload,
  PostSaveRenterSyncPayload,
} from '@src/v3/interfaces/app/propertyLists';
import {
  httpDelete,
  httpGet,
  httpPatch,
  httpPost,
  httpPut,
} from '@src/v3/utils/http';
import { getOriginString } from '@src/v3/utils/location';

/**
 * This method is used to perform API request for retrieving public properties.
 *
 * @returns Promise
 */
const getProperties = async (
  filterURL: string | null,
  usePagination = false,
  limit = 15,
  offset = 0
) => {
  let url = PROPERTIES_API_URL.PUBLIC_PROPERTIES;
  // Convert "Studio" to "0" for API (URL keeps "Studio" for display)
  let apiFilterURL = filterURL;
  if (apiFilterURL) {
    apiFilterURL = apiFilterURL.replace(/bedrooms=([^&]*)/g, (match, value) => {
      const converted = value
        .split(',')
        .map((bed: string) => (bed === 'Studio' ? '0' : bed))
        .join(',');
      return `bedrooms=${converted}`;
    });
  }
  if (apiFilterURL) url += apiFilterURL;
  if (usePagination) {
    url = apiFilterURL
      ? `${url}&limit=${limit}&offset=${offset}`
      : `${url}?limit=${limit}&offset=${offset}`;
  }
  const response = await httpGet(url);

  return response;
};

/**
 * This method is used to perform API request for retrieving public property by id.
 *
 * @returns Promise
 */
const getProperty = async (
  id: string,
  increase_view_count?: boolean,
  hideError?: boolean
) => {
  let url = `${PROPERTIES_API_URL.PROPERTY_ID.replace(
    '#ID#',
    String(id || '')
  )}`;

  if (increase_view_count === false) {
    url += `?increase_view_count=${increase_view_count}`;
  }
  const response = await httpGet(url, { hideError });

  return response;
};

/**
 * This method is used to perform API request for retrieving agent properties.
 *
 * @returns Promise
 */
const getAgentProperties = async (
  agentId: string,
  isSearchURL: string | null,
  offset: number
) => {
  const limit = 20;
  const pageOffset = offset * limit || 0;
  const url = isSearchURL
    ? `${PROPERTIES_API_URL.AGENT_PROPERTIES}/${agentId}${isSearchURL}&limit=${limit}&offset=${pageOffset}`
    : `${PROPERTIES_API_URL.AGENT_PROPERTIES}/${agentId}?limit=${limit}&offset=${pageOffset}`;
  const response = await httpGet(url);

  return response;
};

/**
 * This method is used to perform API request for retrieving agent properties count.
 * Returns counts for open, closed, and all properties.
 *
 * @returns Promise with counts object
 */
const getAgentPropertiesCount = async () => {
  const url = PROPERTIES_API_URL.AGENT_PROPERTIES_COUNT;
  const response = await httpGet(url);

  return response;
};

/**
 * This method is used to perform API request for creating properties.
 * @param FormData
 * @returns Promise
 */
const createProperties = async (params: FormData) => {
  const response = await httpPost(
    `${PROPERTIES_API_URL.PUBLIC_PROPERTIES}`,
    params
  );

  return response;
};

/**
 * This method is used to perform API request for update the properties.
 * @param UpdatePropertyPayload
 * @returns Promise
 */
const updateProperties = async (params: UpdatePropertyPayload) => {
  const { id, payload } = params;
  const response = await httpPatch(
    `${PROPERTIES_API_URL.PROPERTY_ID.replace('#ID#', String(id || ''))}`,
    payload
  );

  return response;
};

/**
 * This method is used to perform API request for update the properties.
 * @param UpdatePropertyPayload
 * @returns Promise
 */
const modifyDocumentsProperties = async (params: ModifyDocumentsProperty) => {
  const { id, payload } = params;
  const response = await httpPut(
    `${PROPERTIES_API_URL.MODIFY_PROPERTY_DOCUMENT.replace(
      '#ID#',
      String(id || '')
    )}`,
    payload
  );

  return response;
};

/**
 * This method is used to perform API request for creating description with chatgpt.
 * @param CreatePropertyPayload
 * @returns Promise
 */
const propertyPreviewBeforePosting = async (params: CreatePropertyPayload) => {
  const response = await httpPost(
    `${PROPERTIES_API_URL.PROPERTY_PREVIEW_BEFORE_POSTING}`,
    params
  );

  return response;
};

const getMapProperties = async (
  agentId = '',
  filterURL: string | null = null
) => {
  let url = PROPERTIES_API_URL.MAP_DATA;
  // Convert "Studio" to "0" for API (URL keeps "Studio" for display)
  let apiFilterURL = filterURL;
  if (apiFilterURL) {
    apiFilterURL = apiFilterURL.replace(/bedrooms=([^&]*)/g, (match, value) => {
      const converted = value
        .split(',')
        .map((bed: string) => (bed === 'Studio' ? '0' : bed))
        .join(',');
      return `bedrooms=${converted}`;
    });
  }
  if (apiFilterURL) {
    url += apiFilterURL;
  }
  const response = await httpGet(
    agentId !== '' ? `${url}?agent_id=${agentId}` : `${url}`
  );
  return response;
};

const openProperty = async (id: string) => {
  const response = await httpPost(
    `${PROPERTIES_API_URL.OPEN_PROPERTY.replace('#ID#', String(id || ''))}`,
    {}
  );
  return response;
};

const closeProperty = async (id: string) => {
  const response = await httpPost(
    `${PROPERTIES_API_URL.CLOSE_PROPERTY.replace('#ID#', String(id || ''))}`,
    {}
  );
  return response;
};

/**
 * This method is used to perform API request for retrieving all save renter property
 *
 * @returns Promise
 */
const getAllRenterSavesProperty = async () => {
  const response = await httpGet(PROPERTIES_API_URL.GET_ALL_SAVE_PROPERTY);
  return response;
};

/**
 * This method is used to perform API request for save renter property.
 * @param {PostSaveRenterSyncPayload} params
 * @returns Promise
 */
const postRenterSaveSync = async (params: PostSaveRenterSyncPayload) => {
  const response = await httpPost(
    PROPERTIES_API_URL.POST_RENTER_SAVE_SYNC,
    params
  );
  return response;
};

/**
 * This method is used to perform API request for unsave renter property.
 * @param {PostSaveRenterSyncPayload} params
 * @returns Promise
 */
const deleteRenterunSaveSync = async (
  params: DeleteUnsaveRenterSyncPayload
) => {
  const response = await httpDelete(
    PROPERTIES_API_URL.DELETE_RENTER_SAVE_SYNC,
    params
  );
  return response;
};

/**
 * Searches for a property based on the provided search query.
 *
 * @param {string} search - The search query to use.
 * @return {Promise<any>} A promise that resolves to the response from the server.
 */
const searchProperty = async (
  search: string,
  restrict: string,
  isAllowGlobalSearch: boolean,
  previousQuery?: string
) => {
  const params: {
    input_query: string;
    restrict?: string;
    global_search?: boolean;
    previous_query?: string;
    origin?: string;
  } = {
    input_query: search,
  };

  if (isAllowGlobalSearch) {
    params['global_search'] = true;
  }

  if (restrict) {
    params['restrict'] = restrict;
  }

  if (previousQuery) {
    params['previous_query'] = previousQuery;
  }

  // Get user location origin (lat,lng format) for search relevance
  const origin = await getOriginString();
  if (origin) {
    params['origin'] = origin;
  }

  const response = await httpPost(PROPERTIES_API_URL.SEARCH_PROPERTY, params);
  return response;
};

/**
 * Retrieves the agent required list template asynchronously.
 *
 * @return {Promise<any>} A promise that resolves to the response from the server.
 */
const getAgentRequiredListTemplate = async () => {
  const response = await httpGet(
    `${PROPERTIES_API_URL.AGENT_TEMPLATE_RETREIVE}`
  );
  return response;
};

/**
 * Retrieves the agent document asynchronously.
 *
 * @return {Promise<any>} A promise that resolves to the response from the server.
 */
const getAgentRequiredListDocuments = async () => {
  const response = await httpGet(`${PROPERTIES_API_URL.AGENT_DOCUMENTS}`);
  return response;
};

const checkDuplicateDocument = async (name: string) => {
  const response = await httpGet(
    `${PROPERTIES_API_URL.CHECK_DUPLICATE_DOCUMENT}?name=${name}`
  );
  return response;
};

const deleteAgentDocument = async (name: string) => {
  const response = await httpDelete(
    `${PROPERTIES_API_URL.DELETE_DOCUMENT}?name=${name}`
  );
  return response;
};

const agentCloseProperty = async (
  propertyId: string,
  params: ClosePropertyProps
) => {
  const url = PROPERTIES_API_URL.AGENT_CLOSE_PROPERTY.replace(
    '#ID#',
    String(propertyId || '')
  );

  const response = await httpPost(url, params);
  return response;
};

/**
 * Invites collaborators (co-listers) to a property.
 * @param propertyId - The ID of the property
 * @param collaborators - Array of collaborators with email and role
 * @returns Promise
 */
const inviteCollaborators = async (
  propertyId: string,
  collaborators: Collaborator[]
) => {
  const url = PROPERTIES_API_URL.INVITE_COLLABORATORS.replace(
    '#ID#',
    String(propertyId)
  );
  const response = await httpPost(url, { collaborators });
  return response;
};

/**
 * Updates the role of a single collaborator on a property.
 * @param propertyId - The ID of the property
 * @param collaboratorId - The ID of the collaborator
 * @param role - The new role for the collaborator
 * @returns Promise
 */
const updateCollaboratorRole = async (
  propertyId: string,
  collaboratorId: string,
  role: string
) => {
  const url = PROPERTIES_API_URL.UPDATE_COLLABORATOR_ROLE.replace(
    '#PROPERTY_ID#',
    String(propertyId)
  ).replace('#COLLABORATOR_ID#', String(collaboratorId));
  const response = await httpPatch(url, { role });
  return response;
};

/**
 * Removes a single collaborator from a property.
 * @param propertyId - The ID of the property
 * @param collaboratorId - The ID of the collaborator to remove
 * @returns Promise
 */
const removeCollaborator = async (
  propertyId: string,
  collaboratorId: string
) => {
  const url = PROPERTIES_API_URL.REMOVE_COLLABORATOR.replace(
    '#PROPERTY_ID#',
    String(propertyId)
  ).replace('#COLLABORATOR_ID#', String(collaboratorId));
  const response = await httpDelete(url);
  return response;
};

/**
 * Leave a property listing (current user removes themselves as collaborator).
 * @param propertyId - The ID of the property
 * @returns Promise
 */
const leaveCollaboration = async (propertyId: string) => {
  const url = PROPERTIES_API_URL.LEAVE_COLLABORATION.replace(
    '#PROPERTY_ID#',
    String(propertyId)
  );
  const response = await httpPost(url, {});
  return response;
};

/**
 * Resends an invite to a collaborator.
 * @param propertyId - The ID of the property
 * @param collaboratorId - The ID of the collaborator to resend invite to
 * @returns Promise
 */
const resendCollaboratorInvite = async (
  propertyId: string,
  collaboratorId: string
) => {
  const url = PROPERTIES_API_URL.RESEND_COLLABORATOR_INVITE.replace(
    '#PROPERTY_ID#',
    String(propertyId)
  ).replace('#COLLABORATOR_ID#', String(collaboratorId));
  const response = await httpPost(url, { hideError: true });
  return response;
};

/**
 * Fetches properties where the current agent has been invited as a collaborator.
 * @param filterURL - Optional query string for filtering/pagination
 * @param offset - Pagination offset
 * @returns Promise with collaboration properties
 */
const getMyCollaborations = async (
  filterURL: string | null = null,
  offset: number = 0
) => {
  const limit = 20;
  const pageOffset = offset * limit || 0;
  let url = PROPERTIES_API_URL.MY_COLLABORATIONS;

  if (filterURL) {
    url = `${url}${filterURL}&limit=${limit}&offset=${pageOffset}`;
  } else {
    url = `${url}?limit=${limit}&offset=${pageOffset}`;
  }

  const response = await httpGet(url);
  return response;
};

/**
 * Accept a collaboration invite using the invite token
 * @param inviteToken - The invite token from my_invite object
 * @returns Promise
 */
const acceptCollaborationInvite = async (inviteToken: string) => {
  const url = PROPERTIES_API_URL.ACCEPT_COLLABORATION;
  const response = await httpPost(url, { token: inviteToken });
  return response;
};

/**
 * Reject a collaboration invite using the invite token
 * @param inviteToken - The invite token from my_invite object
 * @returns Promise
 */
const rejectCollaborationInvite = async (inviteToken: string) => {
  const url = PROPERTIES_API_URL.REJECT_COLLABORATION;
  const response = await httpPost(url, { token: inviteToken });
  return response;
};

export {
  getProperties,
  getProperty,
  getAgentProperties,
  getAgentPropertiesCount,
  createProperties,
  propertyPreviewBeforePosting,
  updateProperties,
  modifyDocumentsProperties,
  getMapProperties,
  openProperty,
  closeProperty,
  getAllRenterSavesProperty,
  postRenterSaveSync,
  deleteRenterunSaveSync,
  searchProperty,
  getAgentRequiredListTemplate,
  getAgentRequiredListDocuments,
  checkDuplicateDocument,
  deleteAgentDocument,
  agentCloseProperty,
  inviteCollaborators,
  updateCollaboratorRole,
  removeCollaborator,
  leaveCollaboration,
  resendCollaboratorInvite,
  getMyCollaborations,
  acceptCollaborationInvite,
  rejectCollaborationInvite,
};
